import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { fetchSource, updateSource } from '@/lib/api'
import type { SourceDetail as SourceDetailType } from '@/lib/api'
import { TypeBadge, DomainBadge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { MarkdownViewer } from '@/components/editor/MarkdownViewer'
import { ArrowLeft, Loader2, FileText, Pencil, Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'

export function SourceDetail() {
  const { sourceId } = useParams<{ sourceId: string }>()
  const [source, setSource] = useState<SourceDetailType | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Title editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editedTitle, setEditedTitle] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!sourceId) return

    setIsLoading(true)
    setError(null)
    fetchSource(sourceId)
      .then(setSource)
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Failed to load source')
      })
      .finally(() => setIsLoading(false))
  }, [sourceId])

  const handleStartEditing = () => {
    setEditedTitle(source?.title || '')
    setIsEditingTitle(true)
  }

  const handleSaveTitle = async () => {
    if (!sourceId || !source) return

    setIsSaving(true)
    try {
      const updated = await updateSource(sourceId, { title: editedTitle.trim() || undefined })
      setSource(updated)
      setIsEditingTitle(false)
    } catch (e) {
      console.error('Failed to save title:', e)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancelEditing = () => {
    setIsEditingTitle(false)
    setEditedTitle('')
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !source) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <p className="text-muted-foreground mb-4">{error || 'Source not found'}</p>
        <Button variant="outline" asChild>
          <Link to="/cards">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Library
          </Link>
        </Button>
      </div>
    )
  }

  // Display title: use title if set, fallback to filename, then 'Untitled Source'
  const displayTitle = source.title || source.filename || 'Untitled Source'

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <header className="border-b border-border pl-6 pr-8 md:pl-8 md:pr-12 lg:pl-12 lg:pr-20 py-6 bg-card">
        <div className="flex items-center gap-4 mb-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/cards">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Library
            </Link>
          </Button>
        </div>

        {/* Editable title */}
        <div className="flex items-center gap-2 mb-3">
          <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
          {isEditingTitle ? (
            <div className="flex items-center gap-2 flex-1">
              <input
                type="text"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                placeholder="Enter source title..."
                className={cn(
                  'flex-1 px-2 py-1 bg-transparent',
                  'text-2xl font-medium',
                  'border-b border-foreground/30',
                  'focus:outline-none focus:border-foreground'
                )}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveTitle()
                  if (e.key === 'Escape') handleCancelEditing()
                }}
              />
              <button
                onClick={handleSaveTitle}
                disabled={isSaving}
                className="p-1 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                title="Save"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" strokeWidth={1.5} />
                )}
              </button>
              <button
                onClick={handleCancelEditing}
                className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                title="Cancel"
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-1">
              <h1 className="text-2xl font-medium">{displayTitle}</h1>
              <button
                onClick={handleStartEditing}
                className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                title="Edit title"
              >
                <Pencil className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-6 text-sm text-muted-foreground">
          {source.project_tag && <span>Project: {source.project_tag}</span>}
          {source.domain_hint && <span>Domain: {source.domain_hint}</span>}
          <span>Ingested: {new Date(source.ingested_at).toLocaleDateString()}</span>
          <span>{source.cards.length} cards created</span>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-auto pl-6 pr-8 md:pl-8 md:pr-12 lg:pl-12 lg:pr-20 py-8 space-y-8">
        {/* Original Content */}
        <section>
          <h2 className="text-lg font-light mb-4">Original Content</h2>
          <MarkdownViewer content={source.raw_text} />
        </section>

        {/* Cards Created */}
        <section>
          <h2 className="text-lg font-light mb-4">
            Cards Created ({source.cards.length})
          </h2>
          {source.cards.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No cards were created from this source
            </p>
          ) : (
            <div className="border border-border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Title</th>
                    <th className="px-4 py-2 text-left font-medium w-32">Type</th>
                    <th className="px-4 py-2 text-left font-medium w-32">Domain</th>
                    <th className="px-4 py-2 text-left font-medium">Summary</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {source.cards.map((card) => (
                    <tr
                      key={card.card_id}
                      className="hover:bg-muted/30 transition-colors align-top"
                    >
                      <td className="px-4 py-3">
                        <Link
                          to={`/cards/${card.card_id}`}
                          className="text-primary hover:underline font-medium"
                        >
                          {card.title || 'Untitled'}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <TypeBadge type={card.type} />
                      </td>
                      <td className="px-4 py-3">
                        <DomainBadge domain={card.domain} />
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {card.summary}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
